import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SystemNoticeRichEditor from '../components/SystemNoticeRichEditor'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { isSystemNoticeContentEmpty } from '../lib/systemNoticeHtml'
import { SYSTEM_NOTICE_CATEGORY_OPTIONS } from '../lib/systemNoticeCategory'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

export default function SystemNoticeNewPage() {
    const navigate = useNavigate()
    const { hasAnyRole } = useAuth()
    const { show } = useToast()
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [category, setCategory] = useState('GENERAL')
    const [showAsPopup, setShowAsPopup] = useState(false)
    const [showInBoard, setShowInBoard] = useState(false)
    const [notifyWeb, setNotifyWeb] = useState(false)
    const [notifyApp, setNotifyApp] = useState(false)
    const [priority, setPriority] = useState(0)
    const [startAt, setStartAt] = useState('')
    const [endAt, setEndAt] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false

    if (!isOperator) {
        return (
            <div className="p-8 text-center">
                <p className="text-red-600">권한이 없습니다.</p>
            </div>
        )
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!title.trim() || isSystemNoticeContentEmpty(content)) {
            show('제목과 내용을 모두 입력해주세요', 'error')
            return
        }

        setSubmitting(true)
        try {
            const payload = {
                title: title.trim(),
                content: content.trim(),
                category,
                showAsPopup,
                showInBoard,
                notifyWeb,
                notifyApp,
                priority: priority || 0,
                startAt: startAt ? new Date(startAt).toISOString() : null,
                endAt: endAt ? new Date(endAt).toISOString() : null
            }

            const res = await api.post('/system-notices', payload)
            const body = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const id = body?.id
            if (id) {
                show('작성되었습니다', 'success')
                navigate('/ops/system-notices')
            } else {
                show('작성은 완료되었으나 ID를 확인하지 못했습니다', 'warning')
                navigate('/ops/system-notices')
            }
        } catch (err) {
            const message = err?.response?.data?.message || '작성에 실패했습니다'
            show(message, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">운영자 공지 작성</h1>
                <button
                    type="button"
                    onClick={() => navigate('/ops/system-notices')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                >
                    목록으로
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border border-slate-200 bg-white p-6">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        제목 <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="공지 제목을 입력하세요"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        카테고리
                    </label>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    >
                        {SYSTEM_NOTICE_CATEGORY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        내용 <span className="text-red-500">*</span>
                    </label>
                    <p className="mb-2 text-xs text-slate-500">
                        서식·링크·이미지(툴바 이미지 버튼)를 사용할 수 있습니다. 이미지는 운영자만 업로드됩니다.
                    </p>
                    <SystemNoticeRichEditor value={content} onChange={setContent} />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">표시 설정</label>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showAsPopup}
                                onChange={(e) => setShowAsPopup(e.target.checked)}
                                className="rounded border-slate-300"
                            />
                            <span className="text-sm">팝업으로 표시</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showInBoard}
                                onChange={(e) => setShowInBoard(e.target.checked)}
                                className="rounded border-slate-300"
                            />
                            <span className="text-sm">대시보드에 표시</span>
                        </label>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">알림 설정</label>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={notifyWeb}
                                onChange={(e) => setNotifyWeb(e.target.checked)}
                                className="rounded border-slate-300"
                            />
                            <span className="text-sm">WEB 알림(SSE) 전송</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={notifyApp}
                                onChange={(e) => setNotifyApp(e.target.checked)}
                                className="rounded border-slate-300"
                            />
                            <span className="text-sm">APP 알림(FCM) 전송</span>
                        </label>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        우선순위
                    </label>
                    <input
                        type="number"
                        value={priority}
                        onChange={(e) => setPriority(Number(e.target.value) || 0)}
                        min="0"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                    <p className="mt-1 text-xs text-slate-500">높을수록 먼저 표시됩니다</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            표시 시작 일시
                        </label>
                        <input
                            type="datetime-local"
                            value={startAt}
                            onChange={(e) => setStartAt(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            표시 종료 일시
                        </label>
                        <input
                            type="datetime-local"
                            value={endAt}
                            onChange={(e) => setEndAt(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={() => navigate('/ops/system-notices')}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        취소
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90 disabled:opacity-50"
                    >
                        {submitting ? '작성 중...' : '작성'}
                    </button>
                </div>
            </form>
        </div>
    )
}
